import { auth, db, functions } from "../config/firebase";
import { Model } from "@beyondrealityapp/core/shared/constants";
import { VirtualBuddy } from "@beyondrealityapp/core/virtual_buddy/classes";
import { VirtualBuddyField } from "@beyondrealityapp/core/virtual_buddy/constants";
import {
  VirtualBuddyFirestoreType,
  VirtualBuddyTemplateType,
  VirtualBuddyType,
} from "@beyondrealityapp/core/virtual_buddy/types";
import { VirtualBuddySkill } from "@beyondrealityapp/core/virtual_buddy/constants";
import { AllowedModules } from "@beyondrealityapp/core/user/types";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
  UpdateData,
  DocumentData,
} from "firebase/firestore";
import { httpsCallable, HttpsCallable } from "firebase/functions";

// --- Callable payload / response types ---

type CreateVirtualBuddyPayload = {
  templateId?: string;
  title?: string;
  description?: string;
  behaviour?: string;
  skills?: VirtualBuddySkill[];
  profilePicture?: string;
  allowedModules?: AllowedModules;
};

type CreateVirtualBuddyResponse = {
  virtualBuddyId: string;
};

type DeleteVirtualBuddyPayload = {
  virtualBuddyId: string;
};

type DeleteVirtualBuddyResponse = {
  status: string;
};

// --- API ---

export const virtualBuddiesApi = createApi({
  reducerPath: "virtualBuddiesApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["VirtualBuddy", "VirtualBuddyTemplates"],
  endpoints: (builder) => ({
    fetchVirtualBuddies: builder.query<VirtualBuddyType[], void>({
      queryFn: async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return { data: [] };
        try {
          const buddies = await _fetchVirtualBuddies(uid);
          return { data: buddies };
        } catch (error) {
          return { error: error as Error };
        }
      },
      async onCacheEntryAdded(
        _arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        await cacheDataLoaded;
        const q = query(
          collection(db, Model.VIRTUAL_BUDDIES),
          where(VirtualBuddyField.OWNER_UID, "==", uid)
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
          updateCachedData(() => {
            return snapshot.docs.map((d) =>
              VirtualBuddy.fromFirestore({
                ...d.data(),
                id: d.id,
              } as VirtualBuddyFirestoreType).toPlainObject()
            );
          });
        });
        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: ["VirtualBuddy"],
    }),

    fetchVirtualBuddy: builder.query<VirtualBuddyType | null, string>({
      queryFn: async (buddyId) => {
        try {
          const buddy = await _fetchVirtualBuddy(buddyId);
          return { data: buddy };
        } catch (error) {
          return { error: error as Error };
        }
      },
      async onCacheEntryAdded(
        buddyId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        await cacheDataLoaded;
        const unsubscribe = onSnapshot(
          doc(db, Model.VIRTUAL_BUDDIES, buddyId),
          (snap) => {
            updateCachedData(() => {
              if (!snap.exists()) return null;
              return VirtualBuddy.fromFirestore({
                ...snap.data(),
                id: snap.id,
              } as VirtualBuddyFirestoreType).toPlainObject();
            });
          }
        );
        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: (_result, _error, buddyId) => [
        { type: "VirtualBuddy", id: buddyId },
      ],
    }),

    fetchVirtualBuddyTemplates: builder.query<
      VirtualBuddyTemplateType[],
      void
    >({
      queryFn: async () => {
        try {
          const templates = await _fetchVirtualBuddyTemplates();
          return { data: templates };
        } catch (error) {
          return { error: error as Error };
        }
      },
      providesTags: ["VirtualBuddyTemplates"],
    }),

    createVirtualBuddy: builder.mutation<
      CreateVirtualBuddyResponse,
      CreateVirtualBuddyPayload
    >({
      queryFn: async (payload) => {
        try {
          const result = await _createVirtualBuddy(payload);
          return { data: result };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["VirtualBuddy"],
    }),

    updateVirtualBuddy: builder.mutation<
      void,
      { buddyId: string; updates: UpdateData<DocumentData> }
    >({
      queryFn: async ({ buddyId, updates }) => {
        try {
          await _updateVirtualBuddy(buddyId, updates);
          return { data: undefined };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: (_result, _error, { buddyId }) => [
        { type: "VirtualBuddy", id: buddyId },
        "VirtualBuddy",
      ],
    }),

    deleteVirtualBuddy: builder.mutation<
      DeleteVirtualBuddyResponse,
      string
    >({
      queryFn: async (virtualBuddyId) => {
        try {
          const result = await _deleteVirtualBuddy(virtualBuddyId);
          return { data: result };
        } catch (error) {
          return { error: error as Error };
        }
      },
      invalidatesTags: ["VirtualBuddy"],
    }),
  }),
});

export const {
  useFetchVirtualBuddiesQuery,
  useFetchVirtualBuddyQuery,
  useFetchVirtualBuddyTemplatesQuery,
  useCreateVirtualBuddyMutation,
  useUpdateVirtualBuddyMutation,
  useDeleteVirtualBuddyMutation,
} = virtualBuddiesApi;

const _fetchVirtualBuddies = async (
  uid: string
): Promise<VirtualBuddyType[]> => {
  const q = query(
    collection(db, Model.VIRTUAL_BUDDIES),
    where(VirtualBuddyField.OWNER_UID, "==", uid)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) =>
    VirtualBuddy.fromFirestore({
      ...d.data(),
      id: d.id,
    } as VirtualBuddyFirestoreType).toPlainObject()
  );
};

const _fetchVirtualBuddy = async (
  buddyId: string
): Promise<VirtualBuddyType | null> => {
  const snap = await getDoc(doc(db, Model.VIRTUAL_BUDDIES, buddyId));
  if (!snap.exists()) return null;
  return VirtualBuddy.fromFirestore({
    ...snap.data(),
    id: snap.id,
  } as VirtualBuddyFirestoreType).toPlainObject();
};

const _fetchVirtualBuddyTemplates =
  async (): Promise<VirtualBuddyTemplateType[]> => {
    const callable: HttpsCallable<void, VirtualBuddyTemplateType[]> =
      httpsCallable(functions, "listVirtualBuddyTemplates");
    const result = await callable();
    return result.data;
  };

const _createVirtualBuddy = async (
  payload: CreateVirtualBuddyPayload
): Promise<CreateVirtualBuddyResponse> => {
  const callable: HttpsCallable<
    CreateVirtualBuddyPayload,
    CreateVirtualBuddyResponse
  > = httpsCallable(functions, "createVirtualBuddy");
  const result = await callable(payload);
  return result.data;
};

const _updateVirtualBuddy = async (
  buddyId: string,
  updates: UpdateData<DocumentData>
): Promise<void> => {
  const ref = doc(db, Model.VIRTUAL_BUDDIES, buddyId);
  await updateDoc(ref, updates);
};

const _deleteVirtualBuddy = async (
  virtualBuddyId: string
): Promise<DeleteVirtualBuddyResponse> => {
  const callable: HttpsCallable<
    DeleteVirtualBuddyPayload,
    DeleteVirtualBuddyResponse
  > = httpsCallable(functions, "deleteVirtualBuddy");
  const result = await callable({ virtualBuddyId });
  return result.data;
};
